/**
 * Global filter state (spec §101).
 *
 * Cascading is enforced here rather than in each dropdown: choosing a VP clears
 * any manager and employee that no longer belong to it, so the UI cannot hold a
 * combination the backend would reject.
 */
import { create } from "zustand";

export type PeriodKey =
  | "today" | "yesterday" | "last_7" | "last_30" | "this_week" | "last_week"
  | "mtd" | "previous_mtd" | "qtd" | "previous_quarter" | "fytd"
  | "previous_fytd" | "custom";

export interface FilterState {
  period: PeriodKey;
  dateFrom: string | null;
  dateTo: string | null;
  compareTo: string | null;
  vp: string | null;
  manager: string | null;
  employee: string | null;
  client: string | null;
  area: string | null;
  branch: string | null;
  set: (patch: Partial<FilterState>) => void;
  reset: () => void;
  queryParams: () => Record<string, string>;
}

const INITIAL = {
  period: "mtd" as PeriodKey,
  dateFrom: null, dateTo: null, compareTo: null,
  vp: null, manager: null, employee: null, client: null, area: null, branch: null,
};

export const useFilterStore = create<FilterState>((set, get) => ({
  ...INITIAL,

  set(patch) {
    // Cascade: a narrower selection cannot survive its parent changing.
    if ("vp" in patch) Object.assign(patch, { manager: null, employee: null });
    if ("manager" in patch) Object.assign(patch, { employee: null });
    set(patch);
  },

  reset() {
    set(INITIAL);
  },

  queryParams() {
    const s = get();
    const out: Record<string, string> = { period: s.period };
    if (s.period === "custom") {
      if (s.dateFrom) out.date_from = s.dateFrom;
      if (s.dateTo) out.date_to = s.dateTo;
    }
    if (s.compareTo) out.compare_to = s.compareTo;
    for (const key of ["vp", "manager", "employee", "client", "area", "branch"] as const) {
      const value = s[key];
      if (value) out[key] = value;
    }
    return out;
  },
}));
